import { zodResolver } from "@hookform/resolvers/zod";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { CountryCode, isValidPhoneNumber, parsePhoneNumberFromString } from "libphonenumber-js";
import { Fragment, useState } from "react";
import { FieldErrors, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { ReactComponent as ShieldCheck } from "@/assets/icons/shield-check.svg";
import { Button, ErrorMessage, FileInput, Icon, Modal, PhoneNumberInput, TextArea, TextInput, Tooltip, Typography } from "@/components/atoms";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import {
  useGetClientBookingDocumentsQuery,
  useLazyGetClientBookingDocumentQuery,
} from "@/redux/apis/clientBooking/clientBookingApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setAdditionalInfo, setBookedBy, setPassengerDetails, setReference } from "@/redux/slices/clientBooking/clientBookingSlice";
import {
  selectAdditionalInfo,
  selectBookedBy,
  selectBookingVehicle,
  selectEditJobId,
  selectNumOfPassengers,
  selectPassengerDetails,
  selectReference,
} from "@/redux/slices/clientBooking/selectors";
import { defaultCountrySelector } from "@/redux/slices/operator/selectors";
import { addToast, clsx } from "@/utils";
import { useBookingAttachments } from "./BookingAttachmentsContext";
import { useBookingViewOnly } from "./BookingViewOnlyContext";
import { ViewOnlyFieldset } from "./ViewOnlyFieldset";
import { useForceValidate } from "./useForceValidate";
import { isMaskedCountry, maskedCountryRows } from "./maskedCountries";
import { useBookedByName } from "./useBookedByPrefill";
import { useMainPassenger } from "./useMainPassenger";

type Layout = "card" | "sheet";

interface OtherDetailsPanelProps {
  /** "card" = desktop side panel; "sheet" = mobile bottom sheet (adds a Close action). */
  layout: Layout;
  /** Called after committing — advances the flow (this is the last sub-step, so it moves on to Payment). */
  onProceed: () => void;
  onClose: () => void;
  /** When true, show field errors immediately (the user was routed here by a failed exit). */
  forceValidate?: boolean;
}

// Validation mirrors the old sheet's Laminas form elements (web-app BookingManager/Form/Element):
//   Booked by       — required, ≤100 (BookedBy.php). Pre-seeded, so it's only empty if cleared.
//   Reference       — optional, ≤100 (ReferenceNumber.php).
//   Additional info — optional, ≤1000 (AdditionalInformation.php).
// Passenger #1 is locked (seeded from "Who is this booking for?") so it never validates here; the
// remaining passenger rows are optional — a name is free text (≤255, Name.php) and a phone is only
// checked for format when entered (the old backend did no phone-format validation; ours is stricter).
const otherDetailsSchema = z.object({
  bookedBy: z.string().min(1, "Booked by is required").max(100, "Booked by must be 100 characters or less"),
  reference: z.string().max(100, "Reference number must be 100 characters or less"),
  additionalInfo: z.string().max(1000, "Notes must be 1000 characters or less"),
  passengers: z.array(
    z.object({
      name: z.string().max(255, "Name must be 255 characters or less"),
      phone: z.string().refine((value) => !value || isValidPhoneNumber(value), "Invalid phone number"),
    })
  ),
});
// Explicit type (not z.infer) so react-hook-form's FieldPath still recurses into the passengers
// array — the phone field's .refine() wraps it in a ZodEffects that otherwise breaks path inference.
type OtherDetailsForm = {
  bookedBy: string;
  reference: string;
  additionalInfo: string;
  passengers: { name: string; phone: string }[];
};

// Attachment limits, aligned to the client document-upload endpoint (Journey BookingDocument →
// FileUpload extends FileImage: 20MB cap). Documents (doc/docx) are intentionally excluded even
// though the backend accepts them — a booking attachment is an image or PDF (e.g. a company logo).
// The backend also accepts these, so anything that passes here won't be rejected server-side.
const ATTACHMENT_ACCEPT = "image/png,image/jpeg,image/gif,application/pdf";
const ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024; // 20 MB
const ATTACHMENT_ALLOWED_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "pdf"];

// The backend gives a masked session to the primary passenger plus 10 others
// (JobMessagingProxyService::MAX_MASKED_PASSENGERS in web-app). The cap is on masked SESSIONS, not
// on passenger rows: maskablePassengers() drops rows with no phone before it counts, so a phoneless
// passenger takes no slot and a job can hold more than 11 rows without exhausting the pool. Holding
// the list itself to 11 rows is a product decision, matching the Figma.
const MAX_MASKED_PASSENGERS = 11;

/** An error message if the picked file isn't an allowed image/PDF within the size cap, else null. */
const validateAttachment = (file: File): string | null => {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!ATTACHMENT_ALLOWED_EXTENSIONS.includes(extension)) return "Unsupported file type. Please use PNG, JPG, GIF or PDF.";
  if (file.size > ATTACHMENT_MAX_BYTES) return "File is too large. The maximum size is 20 MB.";
  return null;
};

/**
 * Shown when the stored booking carries passengers this list can't render — one made before the row
 * cap, on mobile, or on the legacy sheet. viewDraft hydrates every stored row and handleProceed
 * carries the tail through, so those passengers survive each save and their real numbers still
 * reach the driver. The booker is told they exist rather than left to read the list as the whole
 * booking. Keyed on rows that genuinely exist, so a normal booking never sees this.
 */
const HiddenPassengersNote = ({ count }: { count: number }) => (
  <Typography variant="small-regular" className="block text-neutral-silver-gray">
    {count === 1
      ? "1 more passenger is on this booking but can't be edited here. Their number is shared directly with your driver."
      : `${count} more passengers are on this booking but can't be edited here. Their numbers are shared directly with your driver.`}
  </Typography>
);

/** Shown under a phone field the platform can't put behind a proxy number. */
const UnmaskedNote = () => (
  <Typography variant="small-regular" className="block text-neutral-silver-gray">
    Contact masking isn&apos;t available for this country — this number is shared directly with your driver.
  </Typography>
);

/**
 * A form section: label above its control. Mirrors the Field helper in VehicleTransferPanel, plus an
 * optional info icon after the label that explains the field on hover.
 */
const Field = ({ label, tooltip, children }: React.PropsWithChildren<{ label: string; tooltip?: string }>) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center gap-2">
      <Typography variant="action" className="block text-neutral-dark-gray">
        {label}
      </Typography>
      {tooltip && (
        <Tooltip content={tooltip} placement="right" maxWidth={400}>
          <Icon name="InfoCircle" size="sm" className="shrink-0 rotate-180 text-neutral-silver-gray" />
        </Tooltip>
      )}
    </div>
    {children}
  </div>
);

/**
 * "Other Optional Details" — the fourth sub-panel of the Trip Details step. Captures Booked by, Reference
 * Number, additional notes to the driver, and a per-passenger detail list. The list runs to the
 * Vehicle & Transfer step's passenger count, held to the vehicle's seats and to
 * MAX_MASKED_PASSENGERS; row 1 is seeded from the passenger selected in "Who is this booking
 * for?". Committed to clientBookingSlice on Proceed (not live),
 * matching the sibling panels.
 */
export const OtherDetailsPanel = ({ layout, onProceed, onClose, forceValidate = false }: OtherDetailsPanelProps) => {
  const dispatch = useAppDispatch();
  const viewOnly = useBookingViewOnly();
  // Edit mode relabels the commit CTA "Confirm" (it closes the sub-panel rather than advancing).
  const isEdit = useAppSelector(selectEditJobId) != null;
  const numOfPassengers = useAppSelector(selectNumOfPassengers);
  const bookedBy = useAppSelector(selectBookedBy);
  const reference = useAppSelector(selectReference);
  const additionalInfo = useAppSelector(selectAdditionalInfo);
  const storedPassengers = useAppSelector(selectPassengerDetails);
  const vehicle = useAppSelector(selectBookingVehicle);
  // Files live in the sheet-scoped context (File isn't redux-serializable); uploaded after Book Trip.
  const { files, addFiles, removeFile, removedExistingIds, toggleRemoveExisting, clearRemovedExisting } = useBookingAttachments();

  // View/edit of an existing booking (route carries :jobId) → show the attachments already on the job.
  // The create flow has no :jobId, so this skips. `accountId` scopes the fetch under operator OBO.
  const { jobId } = useParams<{ jobId?: string }>();
  const { accountId } = useClientScope();
  const { data: existingDocs = [] } = useGetClientBookingDocumentsQuery(
    jobId ? { jobId: Number(jobId), accountId: accountId ?? undefined } : skipToken
  );
  // Only one attachment is allowed, so the upload button appears only when the single slot is free —
  // i.e. there was never an attachment, or the existing one has been staged for removal.
  const canAddAttachment = existingDocs.every((doc) => removedExistingIds.includes(doc.uuid));
  // The list omits the presigned link, so fetch the single document on demand to open it.
  const [fetchDocument] = useLazyGetClientBookingDocumentQuery();
  const handleDownload = async (uuid: string) => {
    if (!jobId) return;
    try {
      const doc = await fetchDocument({ jobId: Number(jobId), uuid, accountId: accountId ?? undefined }, true).unwrap();
      if (doc.link) window.open(doc.link, "_blank", "noopener,noreferrer");
      else addToast("info", "This attachment isn't available to download.");
    } catch {
      addToast("danger", "Couldn't open the attachment. Please try again.");
    }
  };

  // How many passengers this booking collects details for: the vehicle's seats, held to the
  // product ceiling. No vehicle chosen yet ⇒ only the ceiling applies. The badge and the subtitle
  // both read this, so they can't drift apart, and it must never promise more rows than render.
  // `||` not `??`: a vehicle reporting 0 seats is bad data, not a booking for nobody.
  const passengerCap = Math.min(vehicle?.max_passengers || Number.POSITIVE_INFINITY, MAX_MASKED_PASSENGERS);
  // Vehicle & Transfer already clamps its slider to the vehicle's capacity, so the chosen count
  // only outruns the cap on a draft whose vehicle changed under it. Never below 1 — Passenger #1
  // always renders, and a 0 here would make the carry-over below re-commit them a second time.
  const count = Math.max(1, Math.min(numOfPassengers ?? 1, passengerCap));
  // Passenger #1 is the passenger from "Who is this booking for?" — the chosen guest, the account
  // holder, or (guest flow) the guest's own contact. Locked/prepopulated, derived live via the shared
  // hook so guest and authed both resolve a real name (never "undefined undefined"), never edited here.
  const { name: seedName, phone: seedPhone } = useMainPassenger();
  // Passenger #1's number is seeded from the chosen passenger rather than typed here, so its country
  // comes from the number itself — the locked row has to be as honest about coverage as the rest.
  // Read against the operator's country, the rule normalisePhone follows, because a stored number is
  // not always E.164: "0412 345 678" is unparseable on its own and would libel a maskable AU number.
  const operatorCountry = useAppSelector(defaultCountrySelector) as CountryCode | undefined;
  const seedCountry = parsePhoneNumberFromString(seedPhone ?? "", operatorCountry)?.country;
  // "Booked by" is the person OPERATING the sheet, not the passenger — a different question from
  // seedName above, and the same source useBookedByPrefill seeds the draft from. That hook normally
  // wins (it runs on entering Trip Details, before this panel mounts); this covers the first render
  // if it hasn't landed yet, so the two can't disagree.
  const bookedByName = useBookedByName();
  // Rows the booking holds but this list has no room for. Counted off the stored passengers rather
  // than the vehicle, so it also catches a draft whose vehicle shrank under an existing count.
  // Bounded by numOfPassengers exactly as the carry-over in handleProceed is: rows past the chosen
  // count are dropped on save, so claiming they persist would be the opposite of what happens. That
  // bound also keeps it quiet on the first render, before numOfPassengers has hydrated.
  const hiddenPassengers = Math.max(0, Math.min(storedPassengers?.length ?? 0, numOfPassengers ?? 1) - count);
  // The editable rows are passengers #2..N. A stored row wins over blank so edits survive reopening.
  const extraCount = Math.max(0, count - 1);
  const extraPassengers = Array.from({ length: extraCount }, (_, i) => storedPassengers?.[i + 1] ?? { name: "", phone: "" });

  const {
    control,
    register,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm<OtherDetailsForm>({
    resolver: zodResolver(otherDetailsSchema),
    defaultValues: {
      bookedBy: bookedBy ?? bookedByName,
      reference: reference ?? "",
      additionalInfo: additionalInfo ?? "",
      passengers: extraPassengers,
    },
  });

  // Routed here by a failed "Next Step": surface the field errors (e.g. a cleared "Booked by") now.
  useForceValidate(forceValidate, () => void trigger());

  // The note talks about "this number", so it waits for one — otherwise a blank optional row, or
  // every row under a non-AU operator's default country, would warn about a number nobody entered.
  const watchedPassengers = useWatch({ control, name: "passengers" });

  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [coverageOpen, setCoverageOpen] = useState(false);

  const handleProceed = (form: OtherDetailsForm) => {
    dispatch(setBookedBy(form.bookedBy.trim()));
    dispatch(setReference(form.reference.trim()));
    dispatch(setAdditionalInfo(form.additionalInfo.trim()));
    // Passenger #1 committed from the (locked) selection; the rest from the editable rows.
    dispatch(
      setPassengerDetails([
        { name: seedName.trim(), phone: seedPhone },
        ...form.passengers.map((p) => ({ name: p.name.trim(), phone: p.phone })),
        // Rows the CAP hides are carried through untouched, so a hydrated draft whose vehicle now
        // seats fewer people doesn't silently lose details the booker already entered. Bounded by
        // the chosen count: lowering the passenger count is a removal and must still drop them.
        ...(storedPassengers?.slice(count, numOfPassengers ?? 1) ?? []),
      ])
    );
    onProceed();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <form className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5" onSubmit={handleSubmit(handleProceed)}>
        <Typography variant="h2">Other Optional Details</Typography>
        <Typography variant="muted" className="mt-1 block text-neutral-dark-gray">
          Passenger, Notes, and Others
        </Typography>

        <ViewOnlyFieldset>
          <div className="mt-6 flex flex-col gap-6">
            <Field label="Booked by">
              <TextInput size="md" placeholder="Enter who booked this trip" hasError={!!errors.bookedBy} {...register("bookedBy")} />
              <ErrorMessage errors={errors} name="bookedBy" />
            </Field>

            <Field label="Reference Number" tooltip="Add a reference number for your internal credit card and data reconciliation.">
              <TextInput size="md" placeholder="Enter a reference number" hasError={!!errors.reference} {...register("reference")} />
              <ErrorMessage errors={errors} name="reference" />
            </Field>

            <Field
              label="Additional notes to your driver"
              tooltip="Add any pickup instructions or important details your driver needs for this trip."
            >
              <TextArea
                placeholder="Enter additional notes to your driver"
                maxLength={1000}
                hasError={!!errors.additionalInfo}
                {...register("additionalInfo")}
              />
              <ErrorMessage errors={errors} name="additionalInfo" />
            </Field>

            {/* Single attachment for now (the document endpoint takes one file per request). The
              upload button shows until a file is chosen; remove it to swap. */}
            <Field label="Attachments">
              {/* Existing (already-uploaded) attachments — shown when viewing/editing a booking. Tapping the
                name downloads it; the X stages removal (applied only on "Update Booking", never on tap). */}
              {existingDocs.map((doc) => {
                const staged = removedExistingIds.includes(doc.uuid);
                // Single slot: once a replacement file is picked, hide the staged-for-removal row so there's
                // no Undo path that would leave two attachments. Removing the new file brings it back.
                if (staged && files.length > 0) return null;
                return (
                  <div
                    key={doc.uuid}
                    className="flex items-center gap-2 rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray px-3 py-2"
                  >
                    <Icon name="DocumentText" size="md" className="shrink-0 text-neutral-dark-gray" />
                    <button type="button" onClick={() => void handleDownload(doc.uuid)} className="min-w-0 flex-1 truncate text-left">
                      <Typography
                        variant="paragraph"
                        className={clsx(
                          "truncate text-neutral-dark-gray hover:underline",
                          staged && "text-neutral-silver-gray line-through"
                        )}
                      >
                        {doc.filename}
                      </Typography>
                    </button>
                    {!viewOnly &&
                      (staged ? (
                        <button
                          type="button"
                          aria-label={`Undo remove ${doc.filename}`}
                          onClick={() => toggleRemoveExisting(doc.uuid)}
                          className="shrink-0 text-info"
                        >
                          <Typography variant="small-regular" className="text-info">
                            Undo
                          </Typography>
                        </button>
                      ) : (
                        <button
                          type="button"
                          aria-label={`Remove ${doc.filename}`}
                          onClick={() => toggleRemoveExisting(doc.uuid)}
                          className="shrink-0 text-neutral-dark-gray hover:text-danger"
                        >
                          <Icon name="Add" size="md" className="rotate-45" />
                        </button>
                      ))}
                  </div>
                );
              })}
              {/* A newly-picked file shows as the chosen row. Otherwise the upload button shows only when
                the single attachment slot is free (no active existing attachment). */}
              {files.length > 0 ? (
                <div className="flex items-center gap-2 rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray px-3 py-2">
                  <Icon name="DocumentText" size="md" className="shrink-0 text-neutral-dark-gray" />
                  <Typography variant="paragraph" className="min-w-0 flex-1 truncate text-neutral-dark-gray">
                    {files[0].name}
                  </Typography>
                  <button
                    type="button"
                    aria-label={`Remove ${files[0].name}`}
                    // Cancelling the replacement reverts to the original attachment as a normal active row
                    // (not the struck-through/Undo state) — so it's unmistakably the original, not this file.
                    onClick={() => {
                      removeFile(0);
                      clearRemovedExisting();
                    }}
                    className="shrink-0 text-neutral-dark-gray hover:text-danger"
                  >
                    <Icon name="Add" size="md" className="rotate-45" />
                  </button>
                </div>
              ) : canAddAttachment ? (
                <>
                  <FileInput
                    fullWidth
                    label="Upload file or company logo"
                    accept={ATTACHMENT_ACCEPT}
                    onChange={(e) => {
                      const picked = e.target.files?.[0];
                      e.target.value = "";
                      if (!picked) return;
                      const error = validateAttachment(picked);
                      if (error) {
                        setAttachmentError(error);
                        return;
                      }
                      setAttachmentError(null);
                      addFiles([picked]);
                    }}
                  />
                  {attachmentError ? (
                    <Typography variant="paragraph" className="block text-danger">
                      {attachmentError}
                    </Typography>
                  ) : (
                    <Typography variant="small-regular" className="block text-neutral-silver-gray">
                      PNG, JPG, GIF or PDF · up to 20 MB
                    </Typography>
                  )}
                </>
              ) : null}
            </Field>
          </div>
        </ViewOnlyFieldset>

        {/* Full-bleed divider between the trip fields and the passenger list (20px above/below). */}
        <div className="-mx-5 my-5 h-px shrink-0 bg-neutral-mid-gray" />

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <Typography variant="title" className="block text-neutral-dark-gray">
                Passenger Details
              </Typography>
              <Typography variant="action" className="shrink-0 text-vivid-purple-dark">
                Up to {passengerCap}
              </Typography>
            </div>
            {/* Named only once a vehicle is chosen — there is no sentence to write without one. */}
            {!!vehicle?.name && (
              <Typography variant="paragraph" className="block text-neutral-dark-gray">
                {vehicle.name} up to {passengerCap} passengers
              </Typography>
            )}
          </div>

          {/* Outside ViewOnlyFieldset: reading which countries are covered stays available on a closed
              booking, where the per-field warnings below are still on screen. The sub-line is a plain
              span because Typography's paragraph variant renders a <p>, which a button can't contain. */}
          <button
            type="button"
            onClick={() => setCoverageOpen(true)}
            className="flex w-full items-center gap-2 rounded-md border border-info bg-info-light p-2 text-left"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-info text-white">
              <ShieldCheck className="h-6 w-6" aria-hidden />
            </span>
            <span className="flex min-w-0 flex-1 flex-col text-info-dark">
              <Typography variant="action" className="leading-5">
                Numbers are masked
              </Typography>
              <span className="text-base leading-5">See supported countries</span>
            </span>
            <Icon name="ArrowRight2" size="md" className="shrink-0 text-info" aria-hidden />
          </button>
        </div>

        <ViewOnlyFieldset>
          <div className="mt-6 flex flex-col gap-6">
            {hiddenPassengers > 0 && <HiddenPassengersNote count={hiddenPassengers} />}

            {/* Primary passenger — locked, prepopulated from "Who is this booking for?". */}
            <div className="flex flex-col gap-2">
              <Typography variant="action" className="block text-neutral-dark-gray">
                Primary Passenger #1
              </Typography>
              <TextInput size="md" value={seedName} disabled readOnly />
              <TextInput size="md" value={seedPhone} disabled readOnly />
              {!!seedCountry && !isMaskedCountry(seedCountry) && <UnmaskedNote />}
            </div>

            {/* Passengers #2..N — optional name + phone. */}
            {extraPassengers.map((_, i) => (
              <Fragment key={i}>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <Typography variant="action" className="text-neutral-dark-gray">
                      Passenger #{i + 2}
                    </Typography>
                    <Typography variant="action" className="text-neutral-silver-gray">
                      Optional
                    </Typography>
                  </div>
                  <TextInput size="md" placeholder="Enter Name" {...register(`passengers.${i}.name`)} />
                  <PhoneNumberInput
                    control={control}
                    name={`passengers.${i}.phone`}
                    size="md"
                    hasError={!!errors.passengers?.[i]?.phone}
                    // The full country list stays — an overseas passenger must still be bookable. The
                    // booker is told what that costs instead of being blocked (DEV-1689 tracks the fix).
                    renderCountryNote={(country) => (watchedPassengers?.[i]?.phone && !isMaskedCountry(country) ? <UnmaskedNote /> : null)}
                  />
                  {errors.passengers?.[i] && <ErrorMessage errors={errors.passengers[i] as FieldErrors} name="phone" />}
                </div>
              </Fragment>
            ))}
          </div>
        </ViewOnlyFieldset>
      </form>

      <Modal
        open={coverageOpen}
        onClose={() => setCoverageOpen(false)}
        variant="web-small"
        title="Number Masking"
        description="Calls and messages between passenger and driver are masked. Only these countries are supported."
        renderButtons={() => (
          <Button variant="secondary" size="lg" className="w-full" onClick={() => setCoverageOpen(false)}>
            Close
          </Button>
        )}
      >
        {/* The design draws six countries; the rows come from MASKED_COUNTRIES, which is Australia
            alone. Listing the other five under "Only these countries are supported" would tell an
            operator their UK and NZ passengers are masked when DEV-1689 says they are not. When
            coverage is restored the constant grows and this list grows with it.
            mt-3 takes the 20px the Modal puts under its description up to the design's 32px. */}
        <ul className="mt-3 flex w-full flex-col gap-2.5">
          {maskedCountryRows().map(({ code, flag, name, callingCode }) => (
            <li key={code} className="flex items-center justify-between gap-4">
              <Typography variant="action" className="text-neutral-dark-gray">
                {flag} {name}
              </Typography>
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                + {callingCode}
              </Typography>
            </li>
          ))}
        </ul>
      </Modal>

      {/* Pinned footer: 12px above the buttons with a divider (matches the sibling panels when the body scrolls). */}
      {!viewOnly && (
        <div className="flex flex-col gap-2 border-t border-neutral-mid-gray px-5 pb-5 pt-3">
          <Button variant="primary" size="lg" className="w-full" onClick={handleSubmit(handleProceed)}>
            {isEdit ? "Confirm" : "Proceed"}
          </Button>
          {layout === "sheet" && (
            <Button variant="custom" size="md" className="text-info" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
